import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Tooltip,
} from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import NightlightRoundIcon from "@mui/icons-material/NightlightRound";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import ExitToApp from "@mui/icons-material/ExitToApp";
import AccountCircle from "@mui/icons-material/AccountCircle";
import AdminPanelSettings from "@mui/icons-material/AdminPanelSettings";
import Person from "@mui/icons-material/Person";
import Logout from "@mui/icons-material/Logout";
import PaletteIcon from "@mui/icons-material/Palette";
import CheckIcon from "@mui/icons-material/Check";

import {
  useThemeContext,
  THEME_PRESET_NAMES,
  THEME_PRESET_LABELS,
  ThemePresetName,
} from "../context/ThemeContext";
import { useAuthContext } from "../context/AuthContext";
import { isAdmin } from "../services/AuthService";

const Navbar: React.FC = () => {
  const { toggleTheme, mode, themePreset, setThemePreset } = useThemeContext();
  const { user, logout, loading } = useAuthContext();

  const location = useLocation();
  const navigate = useNavigate();

  // Helper function to safely check admin status
  const checkIsAdmin = () => {
    try {
      return isAdmin();
    } catch (error) {
      console.error("Error checking admin status:", error);
      return false;
    }
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileAnchorEl, setMobileAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [paletteAnchorEl, setPaletteAnchorEl] = useState<null | HTMLElement>(
    null,
  );

  const showAdmin = !loading && user && checkIsAdmin();
  const showPaletteSelector = THEME_PRESET_NAMES.length > 1;

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseProfileMenu = () => setAnchorEl(null);

  const handleLogout = () => {
    logout();
    handleCloseProfileMenu();
    navigate("/");
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileAnchorEl(event.currentTarget);
  };
  const handleMobileMenuClose = () => {
    setMobileAnchorEl(null);
  };

  const handlePaletteClick = (event: React.MouseEvent<HTMLElement>) => {
    setPaletteAnchorEl(event.currentTarget);
  };
  const handleClosePaletteMenu = () => setPaletteAnchorEl(null);

  const handleSelectPreset = (preset: ThemePresetName) => {
    setThemePreset(preset);
    handleClosePaletteMenu();
  };

  return (
    <AppBar position="static" data-testid="navbar">
      <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
        <Box
          display="flex"
          alignItems="center"
          onClick={() => {
            navigate("/");
          }}
          sx={{ cursor: "pointer" }}
        >
          <img
            src="data/logo.svg"
            alt="Logo"
            style={{ width: 40, height: 40 }}
          />
        </Box>

        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            flexGrow: 1,
            justifyContent: "center",
          }}
        >
          <MenuItem
            component={Link}
            to="/"
            selected={location.pathname === "/"}
          >
            Home
          </MenuItem>
        </Box>

        <Box display="flex" alignItems="center">
          {/* Theme Palette Selector — only shown when multiple themes exist */}
          {showPaletteSelector && (
            <>
              <Tooltip title="Change color scheme">
                <IconButton
                  color="inherit"
                  onClick={handlePaletteClick}
                  aria-label="Select color scheme"
                >
                  <PaletteIcon />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={paletteAnchorEl}
                open={Boolean(paletteAnchorEl)}
                onClose={handleClosePaletteMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    px: 2,
                    py: 1,
                    display: "block",
                    color: "text.secondary",
                  }}
                >
                  Color Schemes
                </Typography>
                {THEME_PRESET_NAMES.map((preset) => (
                  <MenuItem
                    key={preset}
                    onClick={() => handleSelectPreset(preset)}
                    selected={themePreset === preset}
                  >
                    <ListItemText>{THEME_PRESET_LABELS[preset]}</ListItemText>
                    {themePreset === preset && (
                      <ListItemIcon sx={{ minWidth: "auto", ml: 1 }}>
                        <CheckIcon fontSize="small" />
                      </ListItemIcon>
                    )}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}

          {/* Light/Dark Toggle */}
          <IconButton
            color="inherit"
            onClick={toggleTheme}
            aria-label={`Switch to ${mode === "light" ? "dark" : "light"} theme`}
          >
            {mode === "light" ? <NightlightRoundIcon /> : <WbSunnyIcon />}
          </IconButton>

          {user ? (
            <>
              <IconButton
                color="inherit"
                onClick={handleProfileClick}
                aria-label="Account menu"
              >
                <AccountCircle data-testid="AccountCircleIcon" />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseProfileMenu}
              >
                <MenuItem
                  onClick={() => {
                    navigate("/account");
                    handleCloseProfileMenu();
                  }}
                >
                  <Person sx={{ mr: 1, fontSize: 20 }} />
                  Account
                </MenuItem>
                {showAdmin && (
                  <MenuItem
                    onClick={() => {
                      navigate("/admin");
                      handleCloseProfileMenu();
                    }}
                    selected={location.pathname.startsWith("/admin")}
                  >
                    <AdminPanelSettings sx={{ mr: 1, fontSize: 20 }} />
                    Admin Panel
                  </MenuItem>
                )}
                <MenuItem onClick={handleLogout}>
                  <Logout sx={{ mr: 1, fontSize: 20 }} />
                  Logout
                </MenuItem>
              </Menu>
            </>
          ) : (
            <IconButton
              color="inherit"
              onClick={() => navigate("/login")}
              aria-label="Login"
            >
              <ExitToApp />
            </IconButton>
          )}

          <IconButton
            edge="start"
            color="inherit"
            aria-label="Open mobile menu"
            onClick={handleMobileMenuOpen}
            sx={{ display: { md: "none" } }}
          >
            <MenuIcon />
          </IconButton>

          <Menu
            anchorEl={mobileAnchorEl}
            open={Boolean(mobileAnchorEl)}
            onClose={handleMobileMenuClose}
          >
            <MenuItem
              onClick={handleMobileMenuClose}
              component={Link}
              to="/"
              selected={location.pathname === "/"}
            >
              Home
            </MenuItem>
            {user && (
              <MenuItem
                onClick={() => {
                  navigate("/account");
                  handleMobileMenuClose();
                }}
              >
                <Person sx={{ mr: 1, fontSize: 20 }} />
                Account
              </MenuItem>
            )}
            {showAdmin && (
              <MenuItem
                onClick={() => {
                  navigate("/admin");
                  handleMobileMenuClose();
                }}
                selected={location.pathname.startsWith("/admin")}
              >
                <AdminPanelSettings sx={{ mr: 1, fontSize: 20 }} />
                Admin Panel
              </MenuItem>
            )}
            {user ? (
              <MenuItem onClick={handleLogout}>
                <Logout sx={{ mr: 1, fontSize: 20 }} />
                Logout
              </MenuItem>
            ) : (
              <MenuItem
                onClick={() => {
                  navigate("/login");
                  handleMobileMenuClose();
                }}
              >
                Login
              </MenuItem>
            )}
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
